/**
 * Simulador de Tarifas de Transporte
 * Con Google Maps autocomplete (carga dinámica) + redirección a app.fuigo.co
 */

import { 
  ErrorHandler, 
  RateLimiter, 
  sanitizeInput, 
  validateAddress,
  formatPrice,
  Analytics
} from './utils.js';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCWlOVOtwVjCKXNouncjYDLQBYkEoNQfQM';

export class TransportSimulator {
  constructor(containerId, config) {
    this.container = document.getElementById(containerId);
    this.apiUrl = config?.apiUrl || '';
    this.fallbackEnabled = config?.fallbackEnabled !== false;
    this.maxResults = config?.maxResults || 5;
    this.rateLimiter = new RateLimiter(10, 60000);
    this.map = null;
    this.directionsRenderer = null;
    
    this.state = {
      origin: null,
      destination: null,
      originCoords: null,
      destCoords: null,
      results: [],
      loading: false,
      error: null,
    };
    
    this.init();
  }

  init() {
    this.render();
    this.attachEventListeners();
    this.loadGoogleMaps();
  }

  /** Carga dinámica de Google Maps (evita warning de loading=async) */
  loadGoogleMaps() {
    if (window.google?.maps?.places) {
      this._setupAutocomplete();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Esperar a que la API esté lista
      const check = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(check);
          this._setupAutocomplete();
        }
      }, 200);
      setTimeout(() => clearInterval(check), 10000);
    };
    script.onerror = () => console.warn('Google Maps no pudo cargarse');
    document.head.appendChild(script);
  }

  _setupAutocomplete() {
    const originInput = document.getElementById('origin-input');
    const destInput = document.getElementById('destination-input');
    if (!originInput || !destInput) return;

    const opts = { componentRestrictions: { country: 'co' }, fields: ['formatted_address', 'geometry', 'name'] };

    try {
      const originAC = new google.maps.places.Autocomplete(originInput, opts);
      const destAC = new google.maps.places.Autocomplete(destInput, opts);

      originAC.addListener('place_changed', () => {
        const place = originAC.getPlace();
        if (place?.geometry?.location) {
          this.state.originCoords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
        }
      });
      destAC.addListener('place_changed', () => {
        const place = destAC.getPlace();
        if (place?.geometry?.location) {
          this.state.destCoords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
        }
      });
    } catch (e) {
      console.warn('Autocomplete setup falló:', e);
    }
  }

  render() {
    if (!this.container) return;
    
    this.container.innerHTML = `
      <div class="space-y-6">
        <div class="grid md:grid-cols-2 gap-4">
          <div>
            <label for="origin-input" class="block text-sm font-medium text-gray-700 mb-2">Origen</label>
            <input type="text" id="origin-input" class="input" placeholder="Ej: Calle 100, Bogotá" aria-label="Dirección de origen" autocomplete="off" />
          </div>
          <div>
            <label for="destination-input" class="block text-sm font-medium text-gray-700 mb-2">Destino</label>
            <input type="text" id="destination-input" class="input" placeholder="Ej: Aeropuerto El Dorado" aria-label="Dirección de destino" autocomplete="off" />
          </div>
        </div>

        <div class="flex gap-4">
          <button id="search-button" class="btn-primary flex-1" aria-label="Comparar precios de transporte">
            <span id="button-text">Comparar Precios</span>
            <span id="button-spinner" class="spinner ml-2 hidden"></span>
          </button>
          <button id="location-button" class="btn-secondary" aria-label="Usar mi ubicación actual" title="Usar mi ubicación actual">📍</button>
        </div>
        
        <div id="results-container" class="hidden">
          <h3 class="text-2xl font-bold mb-4">Resultados estimados</h3>
          <div id="results-list" class="space-y-4"></div>
          <div class="mt-6 text-center">
            <a href="https://app.fuigo.co" class="btn-primary inline-block text-lg px-8 py-3">
              🚀 Ir al App para ver precios reales
            </a>
          </div>
        </div>
        
        <div id="map-container" class="hidden mt-6">
          <div id="map" class="w-full h-64 md:h-80 rounded-lg shadow-md"></div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    const searchBtn = document.getElementById('search-button');
    const locBtn = document.getElementById('location-button');
    const originIn = document.getElementById('origin-input');
    const destIn = document.getElementById('destination-input');
    
    if (searchBtn) searchBtn.addEventListener('click', () => this.handleSearch());
    if (locBtn) locBtn.addEventListener('click', () => this.getCurrentLocation());
    if (originIn) originIn.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.handleSearch(); });
    if (destIn) destIn.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.handleSearch(); });
  }

  async getCurrentLocation() {
    if (!navigator.geolocation) {
      ErrorHandler.display('Tu navegador no soporta geolocalización.', 'info');
      return;
    }
    try {
      const pos = await new Promise((res, rej) => {
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 10000 });
      });
      const { latitude, longitude } = pos.coords;
      this.state.originCoords = { lat: latitude, lng: longitude };

      if (window.google?.maps) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const inp = document.getElementById('origin-input');
            if (inp) inp.value = results[0].formatted_address;
          }
        });
      }
    } catch {
      ErrorHandler.display('No pudimos obtener tu ubicación.', 'warning');
    }
  }

  async handleSearch() {
    const originInput = document.getElementById('origin-input');
    const destInput = document.getElementById('destination-input');
    if (!originInput || !destInput) return;
    
    const origin = sanitizeInput(originInput.value);
    const dest = sanitizeInput(destInput.value);
    
    if (!validateAddress(origin)) { ErrorHandler.display('Ingresa una dirección de origen válida', 'error'); return; }
    if (!validateAddress(dest)) { ErrorHandler.display('Ingresa una dirección de destino válida', 'error'); return; }
    if (!this.rateLimiter.canMakeRequest()) { ErrorHandler.display('Demasiadas búsquedas. Espera un momento.', 'warning'); return; }
    
    this.showLoading(true);
    
    try {
      const results = this.getFallbackPrices();
      this.displayResults(results);
      this.showRoute(origin, dest);
      Analytics.trackSearch(origin, dest);
    } catch {
      ErrorHandler.display('Error al buscar precios.', 'error');
    } finally {
      this.showLoading(false);
    }
  }

  showRoute(origin, destination) {
    if (!window.google?.maps) return;
    
    const mapContainer = document.getElementById('map-container');
    const mapEl = document.getElementById('map');
    if (!mapContainer || !mapEl) return;
    
    mapContainer.classList.remove('hidden');
    
    if (!this.map) {
      this.map = new google.maps.Map(mapEl, {
        center: { lat: 4.711, lng: -74.0721 },
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
        polylineOptions: { strokeColor: '#5A67D8', strokeWeight: 5 },
      });
    }
    
    const directionsService = new google.maps.DirectionsService();
    directionsService.route({
      origin,
      destination,
      travelMode: google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === 'OK') this.directionsRenderer.setDirections(result);
    });
  }

  getFallbackPrices() {
    const base = 12000 + Math.random() * 8000;
    return [
      { service: 'InDriver', icon: '🟢', price: Math.round((base * 0.85) / 100) * 100, duration: '15-25 min', distance: '~8 km', description: 'Negocia tu tarifa' },
      { service: 'DiDi', icon: '🟠', price: Math.round((base * 0.92) / 100) * 100, duration: '12-22 min', distance: '~8 km', description: 'Precios competitivos' },
      { service: 'Yango', icon: '🔴', price: Math.round((base * 0.95) / 100) * 100, duration: '14-22 min', distance: '~8 km', description: 'Buenas tarifas' },
      { service: 'Uber', icon: '🚗', price: Math.round((base * 1.0) / 100) * 100, duration: '12-20 min', distance: '~8 km', description: 'Servicio confiable' },
      { service: 'Cabify', icon: '🟣', price: Math.round((base * 1.1) / 100) * 100, duration: '10-18 min', distance: '~8 km', description: 'Servicio premium' },
    ].sort((a, b) => a.price - b.price);
  }

  displayResults(results) {
    const container = document.getElementById('results-container');
    const list = document.getElementById('results-list');
    if (!container || !list) return;
    
    list.innerHTML = results.map((r, i) => `
      <a href="https://app.fuigo.co" class="block result-card hover:shadow-lg transition-shadow rounded-lg border p-4 bg-white">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span class="text-3xl flex-shrink-0">${r.icon}</span>
            <div class="min-w-0">
              <h4 class="text-lg font-bold truncate">${r.service} ${i === 0 ? '<span class="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-1">💰 Mejor precio</span>' : ''}</h4>
              <p class="text-gray-500 text-xs">${r.description}</p>
              <div class="flex gap-3 text-xs text-gray-500 mt-1">
                <span>⏱️ ${r.duration}</span>
                <span>📍 ${r.distance}</span>
              </div>
            </div>
          </div>
          <div class="text-right flex-shrink-0">
            <div class="text-2xl font-bold text-primary-600">${formatPrice(r.price)}</div>
            <span class="text-xs text-gray-400">Estimado</span>
          </div>
        </div>
      </a>
    `).join('');
    
    container.classList.remove('hidden');
  }

  showLoading(show) {
    const txt = document.getElementById('button-text');
    const spin = document.getElementById('button-spinner');
    const btn = document.getElementById('search-button');
    
    if (show) {
      if (txt) txt.textContent = 'Buscando...';
      if (spin) spin.classList.remove('hidden');
      if (btn) btn.disabled = true;
    } else {
      if (txt) txt.textContent = 'Comparar Precios';
      if (spin) spin.classList.add('hidden');
      if (btn) btn.disabled = false;
    }
  }
}
